import { DeviceKinds as WelderKinds } from '@sb/integrations/implementations/WeldMachine/shared/types/DeviceKinds';
import type { WeldPositionerConfiguration } from '@sb/integrations/implementations/WeldPositioner/types';
import type { EquipmentItem } from '@sb/integrations/types';

import { getRotaryWeldPermittedChildStepKindsShared } from '../../step-rules/weld';
import { type ValidationContext, type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { isUnsetEquipmentRef } from '../../util/unsetEquipmentRef';

import { CanonicalStepRuleBase, stepKindDisplayName } from './base';
import type { CanonicalValidateArgs } from './base';

type RotaryWeldArgs = {
  argumentKind: 'RotaryWeld';
  selectedMachineID?: string | null;
  selectedTorchID?: string | null;
  selectedPositionerID?: string | null;
  completion?: { kind: 'time' | 'positionerSignal' };
  alignmentRequired?: boolean;
};

const WELDER_KINDS = new Set<string>(WelderKinds);

function isWelder(item: EquipmentItem): boolean {
  return WELDER_KINDS.has(item.config.kind);
}

type PositionerItem = EquipmentItem & { config: WeldPositionerConfiguration };

/**
 * A type predicate rather than a boolean: the sensor cross-checks below read
 * `config.completionSensor` and `config.alignmentSensor`, and narrowing to the
 * real configuration type is what makes a rename of either field a compile
 * error here instead of a check that quietly always passes.
 */
function isPositioner(item: EquipmentItem): item is PositionerItem {
  return item.config.kind === 'WeldPositioner';
}

// Any CustomGripper qualifies as a torch, matching the Weld rule, the wizard's
// torch dropdown, and the import sanitizer in `checkEquipmentReferences`.
function isTorch(item: EquipmentItem): boolean {
  return item.config.kind === 'CustomGripper';
}

/**
 * What a rotary weld needs before it can play: a welder, a positioner, and
 * exactly one waypoint for the torch.
 *
 * The positioner's own ports are not checked here — they belong to the
 * equipment, and the equipment manager blocks saving one without a rotation
 * trigger. What this rule cannot see either way is whether the *step's* demands
 * match the positioner's wiring, so the two cross-checks that need both are
 * done here: waiting for a completion signal a positioner has no sensor for,
 * and requiring alignment it cannot report.
 */
class RotaryWeldRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RotaryWeld', 'RotaryWeld');
  }

  protected validate({ args, children, ctx }: CanonicalValidateArgs): void {
    const a = args as unknown as RotaryWeldArgs;
    this.checkMachine(a, ctx);
    this.checkTorch(a, ctx);
    this.checkPositioner(a, ctx);
    this.checkChildStepKinds(children);
    this.checkWaypointCount(children);
  }

  /**
   * @throws {ActionRequiredError}
   */
  private checkMachine(args: RotaryWeldArgs, ctx: ValidationContext): void {
    if (isUnsetEquipmentRef(args.selectedMachineID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Must set a weld machine.',
        fieldId: 'selectedMachineID',
      });
    }

    const machine = ctx.equipment?.find(
      (item) => item.id === args.selectedMachineID,
    );

    if (machine == null || !isWelder(machine)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'The selected weld machine is not on this robot.',
        fieldId: 'selectedMachineID',
      });
    }

    if (!machine.isEnabled) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'The selected weld machine is not enabled.',
        fieldId: 'selectedMachineID',
      });
    }
  }

  /**
   * Ensure a torch is selected, present on the robot, and enabled. The step
   * holds the torch still while the part turns under it, so an unset or
   * missing one is as blocking here as it is for a Weld.
   *
   * @throws {ActionRequiredError}
   */
  private checkTorch(args: RotaryWeldArgs, ctx: ValidationContext): void {
    if (isUnsetEquipmentRef(args.selectedTorchID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Must set a torch.',
        fieldId: 'selectedTorchID',
      });
    }

    const torch = ctx.equipment?.find(
      (item) => item.id === args.selectedTorchID,
    );

    if (torch == null || !isTorch(torch)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'The selected torch is not on this robot.',
        fieldId: 'selectedTorchID',
      });
    }

    if (!torch.isEnabled) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'The selected torch is not enabled.',
        fieldId: 'selectedTorchID',
      });
    }
  }

  /**
   * The PRD's hard requirement: "A Rotary Weld cannot be executed without a
   * configured and enabled positioner equipment device."
   *
   * @throws {ActionRequiredError}
   */
  private checkPositioner(args: RotaryWeldArgs, ctx: ValidationContext): void {
    if (isUnsetEquipmentRef(args.selectedPositionerID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Must set a weld positioner.',
        fieldId: 'selectedPositionerID',
      });
    }

    const positioner = ctx.equipment?.find(
      (item) => item.id === args.selectedPositionerID,
    );

    if (positioner == null || !isPositioner(positioner)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'The selected weld positioner is not on this robot.',
        fieldId: 'selectedPositionerID',
      });
    }

    if (!positioner.isEnabled) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'The selected weld positioner is not enabled.',
        fieldId: 'selectedPositionerID',
      });
    }

    const { config } = positioner;

    // Caught here rather than at play time, where the arc is already the
    // operator's problem: a weld that waits for a signal the cell cannot send
    // would burn until something stops it.
    if (
      args.completion?.kind === 'positionerSignal' &&
      config.completionSensor == null
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Waiting for an input requires a completion sensor on the selected weld positioner.',
        fieldId: 'completion',
      });
    }

    if (args.alignmentRequired === true && config.alignmentSensor == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Weld when aligned requires an alignment sensor on the selected weld positioner.',
        fieldId: 'alignmentRequired',
      });
    }
  }

  /**
   * Only waypoints. `getPermittedChildStepKinds` gates the wizard's add path
   * alone — drag, paste and imported routines reach the step ungated — and the
   * runner's `substeps` is typed as waypoints, so anything else is a lie by the
   * time it plays.
   *
   * @throws {ActionRequiredError}
   */
  private checkChildStepKinds(
    children: Array<{ id: string; stepKind: string }>,
  ): void {
    const permitted = getRotaryWeldPermittedChildStepKindsShared();

    const badChild = children.find(
      (child) => !permitted.includes(child.stepKind as never),
    );

    if (badChild) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Rotary weld steps may not contain ${stepKindDisplayName(
          badChild.stepKind,
        )} steps.`,
      });
    }
  }

  /**
   * Exactly one. The wizard disables its add button and the step definition
   * withholds the decorator placeholder once a waypoint exists, so more than
   * one means an imported or hand-edited routine.
   *
   * @throws {ActionRequiredError}
   */
  private checkWaypointCount(
    children: Array<{ id: string; stepKind: string }>,
  ): void {
    const waypoints = children.filter(
      (child) => child.stepKind === 'Waypoint',
    ).length;

    if (waypoints === 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Rotary weld steps must contain a waypoint for the robot to hold.',
      });
    }

    if (waypoints > 1) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Rotary weld steps support only one waypoint.',
      });
    }
  }
}

export const RotaryWeldValidation: ValidationRule =
  new RotaryWeldRule().toValidationRule();
